import React from "react";
import {
  LEVEL_TYPE,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ExpandTableContainer } from "components/atoms/table/AntdTable";
import { TableColumnsType } from "antd";
import { ExpandableConfig, TableRowSelection } from "antd/es/table/interface";
import * as s from "../StagingStyled";
import SubHeader from "components/blocks/subheader/SubHeader";
import Status, { StatusType } from "components/blocks/status/Status";
import { FiArrowRight, FiCheck } from "react-icons/fi";
import BigButton from "components/atoms/buttons/BigButton";
import {
  AccountInfo,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import AccountId from "components/atoms/text/labels/AccountId";
import { ValidateWord } from "components/atoms/text/Text";
import StagingAssignInfiniteTable from "./StagingAssignInfiniteTable";

type Props = {
  columns: TableColumnsType<any>;
  data: any[];
  rowKey?: string | ((record: any) => React.Key);
  rowSelection?: TableRowSelection<any>;
  loadMore: () => void;
  hasMore: boolean;
  isInitialLoading: boolean;
  expandableProps?: ExpandableConfig<any>;
  onClickSearch: (keyword: string) => void;
  status: StatusType;
  titleComponent: string;
  page: number;
  isCollapsed: boolean;
  onChangeStep: (step: "previous" | "next" | number) => void;
  onClickCancel: () => void;
  selectedAccount?: ProfileAccountInfo;
  selectedAccountRow?: AccountInfo;
  validateMsg: ValidateMsg;
};

export default function StagingAssignPresenter({
  columns,
  data,
  rowKey,
  rowSelection,
  loadMore,
  hasMore,
  isInitialLoading,
  expandableProps,
  onClickSearch,
  status,
  titleComponent,
  page,
  isCollapsed,
  onChangeStep,
  onClickCancel,
  selectedAccount,
  selectedAccountRow,
  validateMsg,
}: Props): JSX.Element {
  const useInfiniteScroll = page === 0 || page === 1;

  return (
    <s.AssignContainer>
      <s.AssignStepBar>
        <s.AssignStepBarItem
          $completed={page > 0}
          $current={page === 0}
          onClick={() => onChangeStep(0)}
        >
          <span>{page > 0 ? <FiCheck /> : 1}</span>
          <b>Recorders</b>
        </s.AssignStepBarItem>
        <s.AssignStepBarItem
          $completed={page > 1}
          $current={page === 1}
          onClick={() => onChangeStep(1)}
        >
          <span>{page > 1 ? <FiCheck /> : 2}</span>
          <b>Organization</b>
        </s.AssignStepBarItem>
        <s.AssignStepBarItem
          $completed={page > 2}
          $current={page === 2}
          onClick={() => onChangeStep(2)}
        >
          <span>{page > 2 ? <FiCheck /> : 3}</span>
          <b>Complete</b>
        </s.AssignStepBarItem>
      </s.AssignStepBar>
      <s.AssignContent $page={page}>
        {page === 2 && (
          <s.AssignComplete>
            <div>
              <AccountId
                accountId={selectedAccount?.accountNumber ?? ""}
                level={selectedAccount?.accountLevel ?? LEVEL_TYPE.CP}
              />
              <span>{selectedAccount?.accountName ?? ""}</span>
            </div>
            <s.AssignCompleteSeparator>
              <FiArrowRight />
            </s.AssignCompleteSeparator>
            <div>
              <AccountId
                accountId={selectedAccountRow?.accountNumber ?? ""}
                level={selectedAccountRow?.level ?? LEVEL_TYPE.EU}
              />
              <span>{selectedAccountRow?.name}</span>
              <span className="email">{selectedAccountRow?.ownerEmail}</span>
            </div>
          </s.AssignComplete>
        )}
        <SubHeader
          titleComponent={titleComponent}
          onClickSearch={onClickSearch}
          noSearch={page === 2}
          centerChildren={
            <>
              <Status status={status} />
              {page === 0 && validateMsg.system && (
                <ValidateWord>{validateMsg.system}</ValidateWord>
              )}
              {page === 1 && validateMsg.account && (
                <ValidateWord>{validateMsg.account}</ValidateWord>
              )}
            </>
          }
        />
        <ExpandTableContainer>
          <StagingAssignInfiniteTable
            columns={columns}
            data={data}
            rowKey={rowKey}
            rowSelection={page === 2 ? undefined : rowSelection}
            loading={isInitialLoading && data.length === 0}
            expandable={page === 2 ? undefined : expandableProps}
            rowClassName="noPointer"
            useInfiniteScroll={useInfiniteScroll}
            loadMore={loadMore}
            hasMore={hasMore}
            isInitialLoading={isInitialLoading}
          />
        </ExpandTableContainer>
      </s.AssignContent>
      <s.AssignButtonWrapper isCollapsed={isCollapsed}>
        <s.AssignButton>
          <BigButton
            label="Cancel"
            buttonType="cancel"
            onClickBtn={onClickCancel}
          />
        </s.AssignButton>
        <s.AssignButton>
          {page > 0 && (
            <BigButton
              label="Previous"
              buttonType="cancel"
              onClickBtn={() => onChangeStep("previous")}
            />
          )}
          <BigButton
            label={page === 2 ? "Assign" : "Next"}
            onClickBtn={() => onChangeStep("next")}
          />
        </s.AssignButton>
      </s.AssignButtonWrapper>
    </s.AssignContainer>
  );
}
